import { Chip } from '@evisort/eds/components/Chip/Chip';
import { Layout } from '@evisort/eds/components/Layout/Layout';
import { Text } from '@evisort/eds/components/Text/Text';
import { Tooltip } from '@evisort/eds/components/Tooltip/Tooltip';
import { useIntl } from 'react-intl';

type ChipItem = {
  text: string;
  onClick?: () => void;
};

type Props = {
  items: ChipItem[] | null | undefined;
};

/**
 * 2.0 `Chip` has no `status`/color variant, so every item renders as a plain
 * chip; `onClick` alone decides whether it's a trigger or a static chip.
 * Unlike 1.0 `Chip`, 2.0's doesn't set `role="listitem"` on itself, so it's
 * added here explicitly — consumers like `DataValuesPanel` query for it.
 */
function ReadChip({ text, onClick }: ChipItem) {
  return (
    <span role="listitem">
      <Tooltip tooltip={text}>
        {onClick ? (
          <Chip label={text} onClick={onClick} />
        ) : (
          <Chip label={text} />
        )}
      </Tooltip>
    </span>
  );
}

export function ReadChipDisplay({ items }: Props) {
  const intl = useIntl();

  if (!items?.length) {
    return (
      <Text tone="subdued" variant="body-s">
        {intl.formatMessage({
          id: 'Field.emptyValue',
          defaultMessage: '(empty)',
          description: 'Displayed when a field has no value',
        })}
      </Text>
    );
  }

  const hasOnClick = items.some((item) => Boolean(item.onClick));
  const shouldRenderAsChips = items.length > 1 || hasOnClick;
  const hasLongText = items.some((item) => item.text.length > 48);

  if (shouldRenderAsChips && hasLongText) {
    return (
      <Layout direction="column" spacing="xs">
        {items.map((item, i) => {
          if (item.onClick) {
            return <ReadChip key={i} text={item.text} onClick={item.onClick} />;
          }
          return (
            <Text tone="auto" key={i} variant="body-s">
              {intl.formatMessage(
                {
                  id: 'ReadChipDisplay.listItem',
                  defaultMessage: '- {text}',
                  description: 'Bullet prefix for a long multi-value list item',
                },
                { text: item.text },
              )}
            </Text>
          );
        })}
      </Layout>
    );
  }

  if (!shouldRenderAsChips && hasLongText) {
    return (
      <Text tone="auto" variant="body-s">
        {items[0].text}
      </Text>
    );
  }

  if (shouldRenderAsChips) {
    return (
      <Layout align="center" direction="row" role="list" spacing="xs" wrap>
        {items.map((item, i) => (
          <ReadChip key={i} text={item.text} onClick={item.onClick} />
        ))}
      </Layout>
    );
  }

  return <ReadChip text={items[0].text} onClick={items[0].onClick} />;
}
